/**
 * 配信詳細ページ（開封・クリック計測の表示。設計書 9.1 / html 図1〜図4・図7）。
 *
 * 設計書: `docs/design-docs/deliveries/open-click-analytics.md`。
 * 本文中の `設計書 N.M` はこの md、`html N.M` / `html 図N` は同ディレクトリの
 * `open-click-analytics.html` を指す。
 */
import { PageTitle } from "components/PageTitle";
import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import {
  type RecipientFilter,
  useDeliveryAnalyticsApi,
  useDeliveryApi,
} from "hooks/use-delivery-api";
import { RefreshCw } from "lucide-react";
import { type FC, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DeliveryRecipientsCard } from "routes/DeliveryPage/components/DeliveryRecipientsCard";
import { DeliveryResultCard } from "routes/DeliveryPage/components/DeliveryResultCard";
import { ReactionBreakdownCard } from "routes/DeliveryPage/components/ReactionBreakdownCard";

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  sent: { label: "送信完了", cls: "badge-success" },
  // biome-ignore lint/style/useNamingConvention: matches backend DeliveryStatus enum value
  partially_sent: { label: "一部失敗", cls: "badge-warning" },
  failed: { label: "失敗", cls: "badge-error" },
  sending: { label: "送信中", cls: "badge-info" },
  scheduled: { label: "予約中", cls: "badge-accent" },
  draft: { label: "下書き", cls: "badge-ghost" },
  cancelled: { label: "キャンセル", cls: "badge-neutral" },
};

/**
 * 配信詳細ページ.
 * 縦順は 配信情報 → 反応の内訳 → 本文 → 宛先一覧（設計書 9.1）。
 * @return {JSX.Element} ページ
 */
export const DeliveryDetailPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [includeBots, setIncludeBots] = useState(false);
  const [recipientFilter, setRecipientFilter] =
    useState<RecipientFilter>("all");
  const recipientsRef = useRef<HTMLDivElement>(null);

  const { data: delivery, loading, error } = useDeliveryApi(id || "");
  /*
    分析は配信の取得を**待たない**。`deliveryId` は最初のレンダーで URL から
    分かっているので、`delivery` に依存させると「配信 1 本を取る」→「分析を取る」
    の 2 波になり、反応データが出るまでに API 往復 1 回ぶん（150〜400ms）が
    まるまる上乗せされる。シリーズ詳細は最初から並列で、そちらに揃える。
    下書き・予約中の配信では 1 回空振りするが、全配信で 1 往復待つより安い
    （空振りしても `measured=false` が返るだけで、エラー表示にはならない）。
  */
  const { data: analytics } = useDeliveryAnalyticsApi(id || "", includeBots);
  // 成績を出すかは**描画の出し分けだけ**に使う（取得のゲートにはしない）
  const showResult =
    !!delivery &&
    delivery.status !== "draft" &&
    delivery.status !== "scheduled";

  /**
   * 不達チップから宛先一覧を「届かなかった宛先のみ」に絞り込む
   * @return {void}
   */
  const handleUndeliveredClick = (): void => {
    setRecipientFilter("undelivered");
    recipientsRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-center py-12">
          <Loading size="lg" />
        </div>
      </div>
    );
  }

  if (error || !delivery) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="mb-4">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate("/deliveries")}
          >
            &larr; 戻る
          </Button>
        </div>
        <div className="alert alert-error">
          <span>{error?.message || "配信メールが見つかりません"}</span>
        </div>
      </div>
    );
  }

  const statusInfo = STATUS_BADGE[delivery.status] || {
    label: delivery.status,
    cls: "badge-ghost",
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      <div className="flex items-center gap-4 mb-6">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate("/deliveries")}
        >
          &larr; 戻る
        </Button>
        <PageTitle>配信詳細</PageTitle>
        <span className={`badge ${statusInfo.cls}`}>{statusInfo.label}</span>
        <div className="ml-auto">
          <Button asChild variant="secondary" size="sm">
            <Link to={`/deliveries/new?copy-from=${id}`}>
              <RefreshCw size={16} />
              再配信
            </Link>
          </Button>
        </div>
      </div>

      <DeliveryResultCard
        delivery={delivery}
        analytics={analytics}
        showResult={showResult}
        includeBots={includeBots}
        onIncludeBotsChange={setIncludeBots}
        onUndeliveredClick={handleUndeliveredClick}
      />

      {analytics?.measured && <ReactionBreakdownCard analytics={analytics} />}

      {/* 本文 */}
      <div className="card bg-base-100 shadow-md mb-4">
        <div className="card-body">
          <h3 className="card-title text-base">本文</h3>
          <div className="bg-base-200 p-4 rounded-lg whitespace-pre-wrap text-sm max-h-80 overflow-y-auto">
            {delivery.bodyText}
          </div>
        </div>
      </div>

      <div ref={recipientsRef}>
        <DeliveryRecipientsCard
          deliveryId={id || ""}
          fromEmail={delivery.fromEmail}
          filter={recipientFilter}
          onFilterChange={setRecipientFilter}
        />
      </div>
    </div>
  );
};
